// VendorWatch Admin Dashboard — Main App Component
// Persona portal: one login, three role-gated workspaces (Sales / Operations /
// Audit). Routes are persona-prefixed (/sales|/ops|/audit) so origin-aware nav
// works; legacy flat routes are retained as transitional aliases until each
// existing page's internal navigation is migrated (T8).
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router';
import { supabase } from '@/lib/supabase';
import { PRODUCT_NAME } from '@/lib/brand';
import { AdminHeader } from '@/components/admin/AdminHeader';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { Toaster } from '@/components/ui/toaster';
import { IdleSessionGuard } from '@/components/auth/IdleSessionGuard';
import { PersonaProvider, usePersona } from '@/context/PersonaProvider';
import { ConsoleReporter } from '@/components/reporter';
import { StudioReporter, closeStudio, useStudioSession } from '@/components/reporter/StudioPanel';
import { AppErrorBoundary, ReportableBoundary } from '@/components/reporter/ReportableBoundary';
import { HOME, personaForPath, type PersonaKey } from '@/lib/persona';
import type { Session } from '@supabase/supabase-js';

// Pages
import { DashboardPage } from '@/pages/Dashboard';
import { LicenseListPage } from '@/pages/LicenseList';
import { LicenseDetailPage } from '@/pages/LicenseDetail';
import { LicenseCreatePage } from '@/pages/LicenseCreate';
import { LicenseValidationsPage } from '@/pages/LicenseValidations';
import { OrganizationListPage } from '@/pages/OrganizationList';
import { OrganizationCreatePage } from '@/pages/OrganizationCreate';
import { OrganizationDetailPage } from '@/pages/OrganizationDetail';
import { SettingsPage } from '@/pages/Settings';
import { SecurityPage } from '@/pages/Security';
import { MfaGate } from '@/components/MfaGate';
import { FleetPage } from '@/pages/Fleet';
import { AccountConsolePage } from '@/pages/AccountConsole';
import { UpdatesPage } from '@/pages/Updates';
import { RemoteOpsPage } from '@/pages/RemoteOps';
import { VulnerabilitiesPage } from '@/pages/Vulnerabilities';
import { MaintenancePage } from '@/pages/Maintenance';
import { OfflineActivationPage } from '@/pages/ops/OfflineActivation';
import { ApiKeyManagerPage } from '@/pages/ApiKeyManager';
import { AdminUsersPage } from '@/pages/AdminUsers';
import { AuditLogsPage } from '@/pages/AuditLogs';
import { LoginPage } from '@/pages/Login';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { AnonymousReporter } from '@/components/reporter/anon/AnonymousReporter';
import { UiSmokePage } from '@/pages/UiSmoke';
import { SalesHome } from '@/pages/sales/SalesHome';
import { OpsHome } from '@/pages/ops/OpsHome';
import { AuditHome } from '@/pages/audit/AuditHome';
import { PricingMatrix } from '@/pages/pricing/PricingMatrix';
import { QuoteBuilder } from '@/pages/pricing/QuoteBuilder';
import { QuotesList } from '@/pages/pricing/QuotesList';
import { QuoteDetail } from '@/pages/pricing/QuoteDetail';
import { PricingAdmin } from '@/pages/pricing/PricingAdmin';
import { SalesAccounts } from '@/pages/sales/SalesAccounts';
import { SalesAccount } from '@/pages/sales/SalesAccount';
import { SalesRenewals } from '@/pages/sales/SalesRenewals';
import { SalesTrials } from '@/pages/sales/SalesTrials';
import { AuditLifecycle } from '@/pages/audit/AuditLifecycle';
import { RolesPermissionsPage } from '@/pages/RolesPermissions';
import { AuditEvidence } from '@/pages/audit/AuditEvidence';
import { ValidationsView, AuditValidations } from '@/components/shared/ValidationsView';
import { ConciergeQueuePage } from '@/pages/trust/ConciergeQueue';
import { TrustOrgMetricsPage } from '@/pages/trust/TrustOrgMetrics';
import { OpportunitiesPage } from '@/pages/trust/Opportunities';
import { CommercialContactsPage } from '@/pages/trust/CommercialContacts';
import { NudgeMatrixPage } from '@/pages/trust/NudgeMatrix';
import { CostGuardrailsPage } from '@/pages/trust/CostGuardrails';
import { DomainsDisputesPage } from '@/pages/trust/DomainsDisputes';
import { SupportTicketsPage } from '@/pages/support/SupportTickets';
import { TicketDetailPage } from '@/pages/support/TicketDetail';
import { StudioPage } from '@/pages/support/Studio';

function CenterSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
      <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
    </div>
  );
}

// Session gate → MFA gate → PersonaProvider for the whole authenticated area.
//
// ── A20 · WHY <ReporterHost> IS HERE AND NOT ONE LEVEL DOWN ──────────────────
// C3 hoisted the reporter provider out of AdminHeader into PortalShell, which
// fixed the boundary/provider ordering for every ROUTED page. It left one screen
// uncovered, and it is the worst one to leave uncovered: <MfaGate>'s blocking
// states.
//
// MfaGate renders INSTEAD of its children while it is challenging or enrolling.
// With the provider inside PortalShell — MfaGate's own child — an operator stuck
// on "Two-factor authentication" had no provider, no button, no buffer and no
// boundary. That is a person who has proved they own the account, cannot get in,
// and whose failing GoTrue mfa.verify() call is sitting in a network buffer that
// was never being recorded. /login has the anonymous reporter precisely because
// "locked out" is when reporting matters most; the MFA gate is the same
// predicament one step further in, and it had nothing.
//
// It takes the AUTHENTICATED reporter, not the anonymous one, because a session
// PROVABLY exists here (the `!session` branch above returned already) and
// POST /reports/mine is fenced by supabaseAuthMiddleware alone — no aal2
// requirement — so an aal1 session is accepted and the route stamps user, email
// and role from the verified token. A named "I cannot complete MFA" ticket is
// worth far more than an anonymous one, and using the anonymous path here would
// throw away an identity we actually hold.
//
// MfaGate was already written for a recorder that would run over it: the
// enrolment QR and the manual TOTP key carry `ps-no-capture` (MfaGate.tsx, the
// `img` and the `code` in the enrol branch) with a comment naming rrweb's
// blockClass — a line number would rot, the two elements will not. That fence
// has never once been
// reachable, because the recorder has never been mounted above this gate — in
// AdminHeader before C3, in PortalShell after it. Now it is, and the fence is
// load-bearing rather than aspirational.
//
// The <ReportableBoundary> here is the same argument applied to crashes: it sits
// INSIDE ReporterHost (a boundary above the provider unmounts the buffer it is
// trying to attach — the inverse arrangement C3 wrote a test for), so a throw
// from MfaGate, PersonaProvider or the chrome in PortalShell now degrades to a
// panel that can still file, instead of falling all the way out to
// <AppErrorBoundary>, whose only honest offer is a mailto.
function ProtectedArea({ session }: { session: Session | null }) {
  if (!session) return <Navigate to="/login" replace />;
  return (
    <ReporterHost>
      <ReportableBoundary>
        <MfaGate>
          <PersonaProvider>
            <Outlet />
          </PersonaProvider>
        </MfaGate>
      </ReportableBoundary>
    </ReporterHost>
  );
}

// Chrome (header switcher + persona sidebar) + role-aware route guard.
function PortalShell() {
  const location = useLocation();
  const { loading, user, canAccess, landingWorkspace, workspaces } = usePersona();
  if (loading) return <CenterSpinner />;

  const urlPersona = personaForPath(location.pathname);
  // Redirect out-of-workspace deep links to the user's default home.
  if (urlPersona && user && !canAccess(urlPersona)) {
    return <Navigate to={landingWorkspace ? HOME[landingWorkspace] : '/settings'} replace />;
  }
  const chrome: PersonaKey = urlPersona ?? landingWorkspace ?? workspaces[0] ?? 'sales';

  return (
    // ── A20 C3 — WHY THE REPORTER PROVIDER IS NOT MOUNTED HERE ────────────
    // It used to be mounted inside AdminHeader, beside the button it feeds.
    // That was fine for the button and wrong for everything else: React context
    // only flows DOWN, so a boundary around <Outlet/> could not see a provider
    // that was its SIBLING, and a page crash would have offered "report this"
    // with no reporter to open — or, with the boundary above the header, would
    // have unmounted the provider and taken the capture buffer with it.
    //
    // C3 moved it here. It now lives one level further up still, in
    // <ProtectedArea>, because MfaGate renders INSTEAD of this component while
    // it is challenging or enrolling and would otherwise be uncovered — see the
    // argument there. The arrangement the shared implementation requires is
    // unchanged and simply spans one more screen:
    //
    //     <ReporterHost>            ← the rolling buffer lives here
    //       <ReportableBoundary>    ← catches MfaGate + this chrome
    //         <MfaGate>             ← draws its own button from context
    //           <AdminHeader/>      ← draws the button from context
    //           <ReportableBoundary>← catches the page, provider survives
    //
    // The "one recorder, ever" rule went up with it, intact: ReporterHost still
    // SWAPS ConsoleReporter and StudioReporter rather than nesting them, and
    // ProtectedArea persists across route changes exactly as the header did, so
    // the recorder still survives navigation.
    <div className="min-h-screen flex flex-col">
      <AdminHeader persona={chrome} />
      <div className="layout-body">
        <AdminSidebar persona={chrome} />
        <main className="main-content">
          {/* A crash in a page degrades to a panel here, with the header,
              the sidebar and the report button all still on screen — and
              the buffer that led to it still held by the provider above. */}
          <ReportableBoundary>
            <Outlet />
          </ReportableBoundary>
        </main>
      </div>
    </div>
  );
}

/**
 * Mounts exactly ONE reporter provider over the authenticated shell.
 *
 * rrweb 2.1.1 keeps `wrappedEmit` at module scope, so a second concurrent
 * record() call HIJACKS the first rather than adding to it — both observer sets
 * emit into the newest buffer and the older recorder goes silently deaf. So the
 * bug reporter and the walkthrough studio are swapped, never nested. That rule
 * was previously enforced inside AdminHeader; hoisting the provider without
 * hoisting the swap would have broken it, which is why both moved together.
 * See components/reporter/StudioPanel.tsx for the full argument.
 */
function ReporterHost({ children }: { children: React.ReactNode }) {
  const studioActive = useStudioSession();
  return studioActive ? (
    <StudioReporter onExit={closeStudio}>{children}</StudioReporter>
  ) : (
    <ConsoleReporter>{children}</ConsoleReporter>
  );
}

// Land the user in their default (persisted-or-first) workspace.
function IndexRedirect() {
  const { loading, landingWorkspace } = usePersona();
  if (loading) return <CenterSpinner />;
  return <Navigate to={landingWorkspace ? HOME[landingWorkspace] : '/settings'} replace />;
}

/**
 * A20 C3 — the outermost net.
 *
 * A Vite SPA has no equivalent of Next's app/global-error.tsx: if the router,
 * the session gate or the chrome throws, React unmounts the whole tree and the
 * user is left with an empty <div id="root">. This wrapper is the only thing
 * between that and a white page.
 *
 * Deliberately OUTSIDE the reporter (which lives inside PortalShell, one node
 * above the routed content): everything it catches has already taken the buffer
 * down with it, so it offers a pre-filled email rather than a report button that
 * would attach nothing. See components/reporter/ReportableBoundary.tsx.
 */
export default function App() {
  return (
    <AppErrorBoundary>
      <AppRoutes />
    </AppErrorBoundary>
  );
}

function AppRoutes() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${PRODUCT_NAME} — Admin`;
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <CenterSpinner />;

  return (
    <BrowserRouter>
      {/* D-52 (19A §6.5): the open-tab half of the session standard. Mounted ONCE, outside
          <Routes>, so it outlives route changes and watches sign-in / sign-out for the whole
          app; it is inert until a session exists. */}
      <IdleSessionGuard />
      <Routes>
        {/* A20 · C1 — the ANONYMOUS reporter, on the two routes that sit outside
            ProtectedArea and therefore outside <ConsoleReporter> (which
            PortalShell's ReporterHost mounts above the authenticated chrome,
            inside the session gate — C3 moved it there from AdminHeader).
            An operator who cannot sign in to the console that administers
            everything else had no way to tell anyone; this is that way. It
            carries route + browser + console + network and NO identity, posts
            to this same server's Turnstile-gated /api/v1/reports/anonymous, and
            renders nothing at all when the site key is unset. */}
        <Route
          path="/login"
          element={
            session ? (
              <Navigate to="/" replace />
            ) : (
              <>
                <LoginPage />
                <AnonymousReporter />
              </>
            )
          }
        />

        {/* Invite / password-recovery set-password landing — outside ProtectedArea
            so it renders whether or not a (recovery) session exists.
            The reporter here is anonymous for the same reason the page is
            unguarded: a RECOVERY session is not a usable console session (it is
            aal1 and may be rejected outright), so there is no bearer worth
            asserting an identity with. */}
        <Route
          path="/reset-password"
          element={
            <>
              <ResetPasswordPage />
              <AnonymousReporter />
            </>
          }
        />

        <Route element={<ProtectedArea session={session} />}>
          <Route element={<PortalShell />}>
            {/* Index → default workspace */}
            <Route path="/" element={<IndexRedirect />} />

            {/* ── Sales workspace ───────────────────────────────────────── */}
            <Route path="/sales" element={<SalesHome />} />
            <Route path="/sales/console" element={<AccountConsolePage />} />
            <Route path="/sales/accounts" element={<SalesAccounts />} />
            <Route path="/sales/accounts/new" element={<OrganizationCreatePage />} />
            <Route path="/sales/accounts/:id" element={<SalesAccount />} />
            <Route path="/sales/renewals" element={<SalesRenewals />} />
            <Route path="/sales/trials" element={<SalesTrials />} />
            <Route path="/sales/licenses" element={<LicenseListPage />} />
            <Route path="/sales/licenses/new" element={<LicenseCreatePage />} />
            <Route path="/sales/licenses/:id/validations" element={<LicenseValidationsPage />} />
            <Route path="/sales/licenses/:id" element={<LicenseDetailPage />} />
            <Route path="/sales/validations" element={<ValidationsView title="Validations" subtitle="Every license validation attempt across accounts" />} />
            <Route path="/sales/pricing" element={<PricingMatrix />} />
            <Route path="/sales/quotes" element={<QuotesList />} />
            <Route path="/sales/quotes/new" element={<QuoteBuilder />} />
            <Route path="/sales/quotes/:id" element={<QuoteDetail />} />
            <Route path="/sales/pricing-admin" element={<PricingAdmin />} />
            <Route path="/sales/trust/leads" element={<ConciergeQueuePage />} />
            <Route path="/sales/trust/org-metrics" element={<TrustOrgMetricsPage />} />
            <Route path="/sales/trust/opportunities" element={<OpportunitiesPage />} />
            <Route path="/sales/trust/contacts" element={<CommercialContactsPage />} />
            <Route path="/sales/trust/nudge-matrix" element={<NudgeMatrixPage />} />
            <Route path="/sales/trust/cost-guardrails" element={<CostGuardrailsPage />} />
            <Route path="/sales/trust/domains-disputes" element={<DomainsDisputesPage />} />

            {/* ── Operations workspace ──────────────────────────────────── */}
            <Route path="/ops" element={<OpsHome />} />
            <Route path="/ops/fleet" element={<FleetPage />} />
            <Route path="/ops/remote-ops" element={<RemoteOpsPage />} />
            <Route path="/ops/vulnerabilities" element={<VulnerabilitiesPage />} />
            <Route path="/ops/updates" element={<UpdatesPage />} />
            <Route path="/ops/maintenance" element={<MaintenancePage />} />
            <Route path="/ops/offline-activation" element={<OfflineActivationPage />} />
            <Route path="/ops/licenses" element={<LicenseListPage />} />
            <Route path="/ops/licenses/:id/validations" element={<LicenseValidationsPage />} />
            <Route path="/ops/licenses/:id" element={<LicenseDetailPage />} />
            <Route path="/ops/validations" element={<ValidationsView title="Validations" subtitle="Operational validation lens — is the install validating?" />} />
            {/* Universal entity views — reachable in-workspace so Fleet→Account/License
                keeps the Ops chrome (the pages are persona-base aware). */}
            <Route path="/ops/accounts" element={<SalesAccounts />} />
            <Route path="/ops/accounts/:id" element={<SalesAccount />} />
            <Route path="/ops/console" element={<AccountConsolePage />} />

            {/* ── Audit workspace (read-only) ───────────────────────────── */}
            <Route path="/audit" element={<AuditHome />} />
            <Route path="/audit/logs" element={<AuditLogsPage />} />
            <Route path="/audit/lifecycle" element={<AuditLifecycle />} />
            <Route path="/audit/evidence" element={<AuditEvidence />} />
            <Route path="/audit/access" element={<AdminUsersPage />} />
            <Route path="/audit/roles" element={<RolesPermissionsPage />} />
            <Route path="/audit/validations" element={<AuditValidations />} />
            {/* Universal entity views (read context preserved in Audit chrome). */}
            <Route path="/audit/accounts" element={<SalesAccounts />} />
            <Route path="/audit/accounts/:id" element={<SalesAccount />} />
            <Route path="/audit/console" element={<AccountConsolePage />} />
            <Route path="/audit/licenses/:id/validations" element={<LicenseValidationsPage />} />
            <Route path="/audit/licenses/:id" element={<LicenseDetailPage />} />

            {/* ── Shared ────────────────────────────────────────────────── */}
            {/* Support triage (A20 W7a). Flat, not persona-prefixed: every
                GitHub issue the reporter files carries this exact deep link
                (github-issues.service.ts builds `<base>/support/tickets/<id>`),
                so the path is a contract with the issue bodies already in the
                tracker and must not move behind a workspace prefix. */}
            <Route path="/support/tickets" element={<SupportTicketsPage />} />
            <Route path="/support/tickets/:id" element={<TicketDetailPage />} />
            {/* A20 W8 — the walkthrough library, flat for the same reason.
                Required by the Support NAV entry: the catch-all below sends any
                unrouted path to '/', so a NAV entry without a route here would
                be a dead link rather than a page. */}
            <Route path="/support/studio" element={<StudioPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/api-keys" element={<ApiKeyManagerPage />} />
            <Route path="/users" element={<AdminUsersPage />} />
            <Route path="/_ui-smoke" element={<UiSmokePage />} />
            <Route path="/executive" element={<DashboardPage />} />

            {/* ── Legacy flat aliases (transitional; existing pages' internal
                   navigation still uses these until migrated in T8) ──────── */}
            <Route path="/account" element={<AccountConsolePage />} />
            <Route path="/licenses" element={<LicenseListPage />} />
            <Route path="/licenses/new" element={<LicenseCreatePage />} />
            <Route path="/licenses/:id/validations" element={<LicenseValidationsPage />} />
            <Route path="/licenses/:id" element={<LicenseDetailPage />} />
            <Route path="/organizations" element={<OrganizationListPage />} />
            <Route path="/organizations/new" element={<OrganizationCreatePage />} />
            <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="/fleet" element={<FleetPage />} />
            <Route path="/updates" element={<UpdatesPage />} />
            <Route path="/remote-ops" element={<RemoteOpsPage />} />
            <Route path="/vulnerabilities" element={<VulnerabilitiesPage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/audit-logs" element={<AuditLogsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}
